import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import {
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Loader2,
  Home,
  AlertCircle,
  Building2,
  User,
} from "lucide-react";

type PageStatus = "loading" | "success" | "error";

/**
 * SubscriptionCreated - Success page for new subscriptions
 * 
 * Route: /subscriptions/:subscriptionId/success
 * - If subscriptionId is "new", looks up subscription by session_id query param
 * - Otherwise fetches subscription directly by ID
 */
export function SubscriptionCreated() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const [searchParams] = useSearchParams();
  const { data: session } = useSession();
  const [status, setStatus] = useState<PageStatus>("loading");
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(5);

  const sessionId = searchParams.get("session_id");

  // Check if we need to look up by session (subscriptionId is "new")
  const isLookupBySession = subscriptionId === "new";
  const hasRealSubscriptionId = subscriptionId && subscriptionId !== "new";

  // If we have a real subscriptionId from route params, fetch subscription directly
  const { data: subscriptionData, isLoading: subscriptionLoading } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!hasRealSubscriptionId && !!session }
  );

  // If subscriptionId is "new", look up subscription by session_id
  const { data: sessionSubData, isLoading: sessionSubLoading, refetch: refetchSessionSub } = trpc.subscription.mutation.getSubscriptionByStripeSession.useQuery(
    { sessionId: sessionId! },
    {
      enabled: !!sessionId && isLookupBySession && !!session,
      retry: false, // We handle retry manually
    }
  );

  // Redirect to proper URL once we find subscription from session
  useEffect(() => {
    if (isLookupBySession && sessionSubData?.subscription) {
      // Found subscription - redirect to the canonical URL
      navigate(`/subscriptions/${sessionSubData.subscription.id}/success`, { replace: true });
    }
  }, [isLookupBySession, sessionSubData, navigate]);

  // Poll for subscription if webhook hasn't processed yet
  useEffect(() => {
    if (isLookupBySession && sessionSubData?.pending && retryCount < 10) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refetchSessionSub();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isLookupBySession, sessionSubData, retryCount, refetchSessionSub]);

  // Set success status when data is loaded
  useEffect(() => {
    if (hasRealSubscriptionId && subscriptionData) {
      setStatus("success");
    }
  }, [hasRealSubscriptionId, subscriptionData]);

  // Error after too many retries
  useEffect(() => {
    if (retryCount >= 10 && sessionSubData?.pending) {
      setStatus("error");
    }
  }, [retryCount, sessionSubData]);

  // Countdown for auto-redirect
  useEffect(() => {
    if (status === "success" && countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === "success" && countdown === 0) {
      // Redirect to subscription page
      if (subscriptionId) {
        navigate(`/subscriptions/${subscriptionId}`);
      }
    }
  }, [status, countdown, navigate, subscriptionId]);

  // Loading state
  const isLoading = sessionSubLoading || subscriptionLoading ||
    (isLookupBySession && !sessionSubData?.subscription) ||
    status === "loading";

  if (isLoading && status !== "error") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-neon-green mx-auto mb-4" />
          <p className="text-chromium-mist">Confirming your subscription...</p>
          {retryCount > 0 && (
            <p className="text-xs text-chromium-mist/70 mt-2">
              Processing payment... ({retryCount}/10)
            </p>
          )}
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card className="max-w-lg w-full border-red-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-silica mb-2">
                Something went wrong
              </h2>
              <p className="text-chromium-mist mb-6">
                We couldn't confirm your subscription. If you were charged, your subscription should activate shortly. Please check your account or contact support.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" asChild>
                  <Link to="/">Go to Account</Link>
                </Button>
                <Button asChild>
                  <a href="mailto:support@nosible.com">Contact Support</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const owner = subscriptionData?.owner
  const isOrganization = owner?.type === "organization" ? true : false;

  return (
    <div className="w-full p-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
      <Card className="max-w-lg w-full border-neon-green/50">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 relative">
            <div className="absolute inset-0 bg-neon-green/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-neon-green/20 rounded-full p-4">
              <CheckCircle2 className="h-16 w-16 text-neon-green" />
            </div>
          </div>
          <CardTitle className="text-2xl text-silica">
            Subscription Created!
          </CardTitle>
          <CardDescription className="text-chromium-mist">
            Your new subscription is now active
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Owner Info */}
          {owner && (
            <div className="flex items-center justify-center gap-2 text-chromium-mist">
              {isOrganization ? (
                <Building2 className="h-4 w-4" />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span>{isOrganization ? "Organization" : "Personal"}: {owner.name}</span>
            </div>
          )}

          {/* Plan Info */}
          <div className="bg-slate-core/50 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-neon-green" />
              <span className="text-lg font-semibold text-silica capitalize">
                {subscriptionData?.plan.name || "New"} Plan
              </span>
            </div>
            <p className="text-sm text-chromium-mist">
              You now have access to all features included in your plan
            </p>
          </div>

          {/* What's Next */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-silica">What's next?</h3>
            <ul className="space-y-2 text-sm text-chromium-mist">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>Create API keys to start using the Nosible API</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>Check out our documentation for integration guides</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-neon-green shrink-0 mt-0.5" />
                <span>View your usage and manage your subscription anytime</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {subscriptionId && (
              <Button asChild className="w-full bg-neon-green text-obsidian hover:bg-neon-green/90">
                <Link to={`/subscriptions/${subscriptionId}`}>
                  View Subscription
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            )}
            <Button variant="outline" asChild className="w-full">
              <Link to={isOrganization && owner?.slug ? `/organizations/${owner.slug}` : "/"}>
                <Home className="h-4 w-4 mr-2" />
                {isOrganization ? "Go to Organization" : "Go to Account"}
              </Link>
            </Button>
          </div>

          {/* Auto-redirect notice */}
          <p className="text-xs text-chromium-mist/70 text-center">
            Redirecting to subscription in {countdown} seconds...
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
